<template>
  <w-stack id="ai-assistant-chat" direction="column" align-items="stretch" full-height>
    <div class="chat-body" ref="chatBody" v-if="actionState !== 'review'">
      <div v-for="(message, index) in transcriptionMessages" :key="index">
        <div class="chat-message-container user" v-if="message">
          <div>
            <div class="chat-message user">
              <div v-html="message"></div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="isTyping" class="chat-message typing">
        <w-text><w-spinner size="small" /></w-text>
      </div>
    </div>
    
    <w-icon-button
      icon-name="arrow-down"
      icon-color="secondary"
      v-if="showScrollButton"
      class="scroll-button"
      @click="scrollToBottom"
      :aria-label="$t('chat.scrollToBottomLabel')"
    />
  </w-stack>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, nextTick, computed, inject, onUnmounted } from "vue";
import { marked } from "marked";
import { useI18n } from "vue-i18n";
import { v4 as uuid } from "uuid";

const { t, locale } = useI18n();

const props = defineProps({
  initialContext: Object,
  transcriptionMessages: {
    type: Array,
    default: () => []
  },
  generatingTranscription: Boolean
});

const patient = computed(() => {
  const birthDate = props.initialContext?.patient?.birthDate;
  const age = birthDate ? new Date().getFullYear() - new Date(birthDate).getFullYear() : null;
  const initialGender = props.initialContext?.patient?.gender;
  let gender = null;
  
  if (`${initialGender}`.toLowerCase().startsWith("m")) {
    gender = t("patient.genderMale");
  } else if (`${initialGender}`.toLowerCase().startsWith("f")) {
    gender = t("patient.genderFemale");
  }
  
  return {
    ...props.initialContext?.patient,
    age,
    gender,
  };
});

const patientTitle = computed(() => {
  return [
    patient.value.name,
    patient.value.gender,
    patient.value.age ? `${patient.value.age} ${t("patient.yearsOld")}` : null,
  ]
    .filter(Boolean)
    .join(", ");
});

const isTyping = ref(false);
const showScrollButton = ref(false);
const chatBody = ref<HTMLElement | null>(null);
const actionState = ref<string | null>(null);

const scrollToBottom = () => {
  if (chatBody.value) {
    chatBody.value.scrollTop = chatBody.value.scrollHeight;
  }
};

const onScroll = () => {
  if (chatBody.value) {
    const isAtBottom = chatBody.value.scrollHeight - chatBody.value.scrollTop === chatBody.value.clientHeight;
    showScrollButton.value = !isAtBottom;
  }
};
</script>

<style scoped lang="scss">
@import "style-variables.scss";

.chat-body {
  flex-grow: 1;
  padding: 16px;
  overflow-y: auto;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  scrollbar-color: rgba(11, 20, 26, 0.2) rgba(0, 0, 0, 0);
  scrollbar-width: thin;
}

.chat-message-container {
  margin-bottom: 8px;
  
  &.user {
    align-self: flex-end;
  }
  
  &.system {
    align-self: center;
  }
}

.chat-message {
  border-radius: 4px;
  max-width: 320px;
  padding: 8px 16px;
  text-align: left;
  
  &.user {
    align-self: flex-end;
    background-color: #f5f5f5;
    
    .w-text {
      color: var(--w-text-color-primary);
      text-align: right;
    }
  }
  
  &.system {
    align-self: center;
    background-color: #f5f5f5;
    
    .w-text {
      color: var(--w-text-color-primary);
      text-align: right;
    }
  }
  
  &.assistant {
    background-color: #f3f0fa;
    
    .w-text {
      color: $primary-color;
      text-align: left;
    }
  }
  
  &.typing {
    font-style: italic;
  }
}

.scroll-button {
  position: absolute;
  bottom: 105px;
  backdrop-filter: blur(10px);
  right: 0;
  left: 0;
  margin-inline: auto;
  border: none;
  border-radius: 50%;
  background-color: #f5f5f5c3;
  
  &:hover {
    background-color: #ebebeb;
  }
}
</style>
